import { http, HttpResponse } from 'msw';
import {
  mockEnterpriseCodes,
  mockProductCodes,
  mockCodePackages,
  mockTraceCodes,
  mockVerificationLogs,
  mockTraceStatsSummary,
  sampleFullChainData,
} from '../fixtures/traceCode';
import type { CodePackage, TraceCode, TraceCodeGenerateParams, OrderSummary } from '@trace/shared/types';
import { getOrdersStore } from './_sharedStores';

let traceCodesStore = [...mockTraceCodes];
let codePackagesStore = [...mockCodePackages];
let verificationLogsStore = [...mockVerificationLogs];
// ordersStore 使用共享模块 — 与 processing.ts 保持同步

export const traceCodeHandlers = [
  // ================= 0. Orders List for Trace Code Association =================
  http.get('/api/tracecode/orders', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);

    let list = [...getOrdersStore()];
    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((o) => String(o.enterpriseId) === String(enterpriseId));
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 1. Full Assigned Trace Codes List =================
  http.get('/api/tracecode/list', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);
    const productId = url.searchParams.get('productId');
    const isVerified = url.searchParams.get('isVerified');
    const keyword = url.searchParams.get('keyword')?.toLowerCase();

    let list = [...traceCodesStore];

    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((tc) => String(tc.enterpriseId) === String(enterpriseId));
    }
    if (productId && productId !== 'all') {
      list = list.filter((tc) => tc.productId === productId);
    }
    if (isVerified && isVerified !== 'all') {
      const verifiedBool = isVerified === 'verified';
      list = list.filter((tc) => tc.isVerified === verifiedBool);
    }
    if (keyword) {
      list = list.filter(
        (tc) =>
          tc.code.toLowerCase().includes(keyword) ||
          tc.productName.toLowerCase().includes(keyword) ||
          tc.enterpriseName.toLowerCase().includes(keyword) ||
          (tc.processingBatchNumber && tc.processingBatchNumber.toLowerCase().includes(keyword)) ||
          (tc.orderNumber && tc.orderNumber.toLowerCase().includes(keyword)),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 2. Code Packages (Export & Print) =================
  http.get('/api/tracecode/packages', ({ request }) => {
    const url = new URL(request.url);
    const headerEnterpriseId = request.headers.get('X-Tenant-Enterprise-Id');
    const enterpriseId = url.searchParams.get('enterpriseId') || (headerEnterpriseId && headerEnterpriseId !== 'all' ? headerEnterpriseId : null);
    const productId = url.searchParams.get('productId');
    const isDownloaded = url.searchParams.get('isDownloaded');
    const keyword = url.searchParams.get('keyword')?.toLowerCase();

    let list = [...codePackagesStore];

    if (enterpriseId && enterpriseId !== 'all') {
      list = list.filter((pkg) => String(pkg.enterpriseId) === String(enterpriseId));
    }
    if (productId && productId !== 'all') {
      list = list.filter((pkg) => pkg.productId === productId);
    }
    if (isDownloaded && isDownloaded !== 'all') {
      const downloadedBool = isDownloaded === 'downloaded';
      list = list.filter((pkg) => pkg.isDownloaded === downloadedBool);
    }
    if (keyword) {
      list = list.filter(
        (pkg) =>
          pkg.packageNumber.toLowerCase().includes(keyword) ||
          (pkg.productName || '').toLowerCase().includes(keyword) ||
          (pkg.enterpriseName || '').toLowerCase().includes(keyword) ||
          (pkg.harvestBatchNo || '').toLowerCase().includes(keyword) ||
          (pkg.orderNumber && pkg.orderNumber.toLowerCase().includes(keyword)),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Mark package as downloaded
  http.post('/api/tracecode/packages/:id/download', ({ params }) => {
    const idx = codePackagesStore.findIndex((p) => p.id === params.id);
    if (idx !== -1) {
      codePackagesStore[idx] = {
        ...codePackagesStore[idx]!,
        isDownloaded: true,
        lastDownloadedAt: new Date().toISOString(),
        downloadCount: (codePackagesStore[idx]!.downloadCount || 0) + 1,
      };
      return HttpResponse.json({
        code: 200,
        message: '已成功下载二维码印刷文件并更新下载状态',
        data: codePackagesStore[idx],
        success: true,
        timestamp: new Date().toISOString(),
      });
    }
    return HttpResponse.json({ code: 404, message: 'Package not found', success: false }, { status: 404 });
  }),

  // ================= 3. Batch Generate Codes =================
  http.post('/api/tracecode/generate', async ({ request }) => {
    const body = (await request.json()) as TraceCodeGenerateParams;
    const quantity = body.quantity || 1000;
    const itemsPerBox = body.itemsPerBox || 1;
    const totalBoxes = Math.ceil(quantity / itemsPerBox);
    const prefix = `${body.enterpriseCode}.${body.productCode}`;
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const mark = body.organicMark || 'ORG';

    const startCode = `${prefix}/${dateStr}.${mark}.0001`;
    const endCode = `${prefix}/${dateStr}.${mark}.${String(quantity).padStart(4, '0')}`;
    const pkgNum = `PKG-${dateStr}-${String(codePackagesStore.length + 1).padStart(3, '0')}`;

    const procCodes = (body as any).processingTaskCodes || [];
    const prodCodes = (body as any).productionTaskCodes || [];
    const procBatchStr = procCodes.length > 0 ? procCodes.join(', ') : (body.processingBatchNumber || `MES-PROC-${dateStr}-001`);

    // Create Package
    const newPackage: CodePackage = {
      id: `pkg_${Date.now()}`,
      packageNumber: pkgNum,
      orderId: body.orderId,
      orderNumber: body.orderNumber,
      customerName: body.customerName,
      harvestBatchNo: procBatchStr,
      enterpriseId: body.enterpriseId,
      enterpriseName: body.enterpriseName || '五常市金穗生态大米加工有限公司',
      enterpriseCode: body.enterpriseCode,
      productId: body.productId,
      productName: body.productName || '5kg 有机胚芽米六角真空礼盒装',
      productCode: body.productCode,
      totalQuantity: quantity,
      itemsPerBox,
      totalBoxes,
      startCode,
      endCode,
      generatedAt: new Date().toISOString(),
      isDownloaded: false,
      downloadCount: 0,
      zipFileUrl: `/api/tracecode/packages/download?id=${pkgNum}`,
      fileSizeBytes: quantity * 750,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    codePackagesStore = [newPackage, ...codePackagesStore];

    // Create Sample Trace Code Records for visualization
    const newTraceCode: TraceCode = {
      id: `tc_${Date.now()}`,
      code: startCode,
      orderId: body.orderId,
      orderNumber: body.orderNumber,
      customerName: body.customerName,
      enterpriseId: body.enterpriseId,
      enterpriseName: body.enterpriseName || '五常市金穗生态大米加工有限公司',
      enterpriseCode: body.enterpriseCode,
      productId: body.productId,
      productName: body.productName || '5kg 有机胚芽米六角真空礼盒装',
      productCode: body.productCode,
      landId: body.landId,
      landName: body.landName || '五常核心有机基地 · 1号稻田A区',
      processingBatchNumber: procBatchStr,
      workshopName: body.workshopName || '五常金穗 10万级GMP标准智能加工车间',
      itemsPerBox,
      status: 'active',
      queryCount: 0,
      isVerified: false,
      isAuthentic: true,
      generatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    traceCodesStore = [newTraceCode, ...traceCodesStore];

    return HttpResponse.json({
      code: 200,
      message: `成功批量生成 ${quantity} 枚 MA 溯源码 (共 ${totalBoxes} 盒/箱，每盒装入 ${itemsPerBox} 件)！`,
      data: {
        package: newPackage,
        startCode,
        endCode,
        quantity,
        itemsPerBox,
        totalBoxes,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 4. Stats & Real-time Verification Logs =================
  http.get('/api/tracecode/stats', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        summary: {
          ...mockTraceStatsSummary,
          totalCodesGenerated: traceCodesStore.length > 5 ? 128500 + (traceCodesStore.length * 1000) : 128500,
        },
        logs: verificationLogsStore,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // ================= 5. Chain Query =================
  http.get('/api/tracecode/chain', ({ request }) => {
    const url = new URL(request.url);
    const code = url.searchParams.get('code') || 'MA.156.A1.0058.AG.101/20260812.ORG.0010';

    let entName = '五常市金穗生态大米加工有限公司';
    let prodName = '5kg 有机胚芽米六角真空礼盒装 (五常稻花香2号)';
    let orderNo = 'ORD-20260826-008';
    let customerName = '盒马鲜生 · 华北区域五常有机胚芽米中秋定制单';
    let notaryInfo = {
      notaryNumber: '(2026)黑哈五证字第8821号',
      notaryOffice: '黑龙江省五常市公证处',
      notaryTitle: '2026年度五常有机稻米全生命周期溯源数据保全与赋码公证书',
      notaryOfficer: '刘振华 / 李立公证员',
      notaryDate: '2026-08-15',
      sha256Hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      fileName: '五常市公证处_有机水稻全链条数据保全公证书.pdf',
      fileUrl: 'https://images.unsplash.com/photo-1544717305-2782549b5136?w=1200',
      fileSize: '6.48 MB',
    };

    if (code.includes('0079') || code.includes('EN.401')) {
      entName = '英式（Engnice）营养食品科技有限公司';
      prodName = '英式 · 低敏有机胚芽米 2.5kg 婴幼儿辅食标准装';
      orderNo = 'ORD-20260829-012';
      customerName = '英式母婴连锁 · 华东直供专属定制单';
      notaryInfo = {
        notaryNumber: '(2026)湘长星证字第0945号',
        notaryOffice: '湖南省长沙市星城公证处',
        notaryTitle: '英式Engnice低敏有机胚芽米原料进厂与出厂全项质检公证认证函',
        notaryOfficer: '周建国 公证员',
        notaryDate: '2026-08-29',
        sha256Hash: '4b227777d4dd1fc61c6f884f48641d02b4d121d3fd328cb08b5531fcacdabf8a',
        fileName: '长沙星城公证处_英式低敏有机米公证认证书.pdf',
        fileUrl: 'https://images.unsplash.com/photo-1544717305-2782549b5136?w=1200',
        fileSize: '4.89 MB',
      };
    } else if (code.includes('0059') || code.includes('SE.201')) {
      entName = '齐齐哈尔嫩江绿色农业加工厂';
      prodName = '禾本真 · 嫩江天然高寒有机富硒黑米 5kg';
      orderNo = 'ORD-20260824-002';
      customerName = '东方甄选 · 嫩江天然富硒生态米专场定制单';
      notaryInfo = {
        notaryNumber: '(2026)黑齐嫩证字第3042号',
        notaryOffice: '黑龙江省嫩江市公证处',
        notaryTitle: '嫩江天然高寒有机富硒黑米原产地溯源赋码专项目录公证书',
        notaryOfficer: '王明远 公证员',
        notaryDate: '2026-08-20',
        sha256Hash: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08',
        fileName: '嫩江市公证处_富硒黑米赋码专项公证书.pdf',
        fileUrl: 'https://images.unsplash.com/photo-1544717305-2782549b5136?w=1200',
        fileSize: '5.23 MB',
      };
    }

    const farmingStages = [
      {
        id: 'stage_01',
        stageCode: 'seeding',
        stageName: '选种浸种与智能温汤催芽',
        startDate: '2026-04-10',
        endDate: '2026-04-20',
        operator: '李春山 (首席农艺师)',
        description: '选用国家审定五优稻4号有机原种。采用60°C恒温温汤浸种杀菌技术，杜绝化学包衣剂，暗室智能恒温恒湿出苗。',
        photos: [
          'https://images.unsplash.com/photo-1592982537447-7440770cbfc9?w=600',
          'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=600',
        ],
        inputs: [
          { name: '五优稻4号有机水稻原种 (发芽率≥98%)', type: '有机种子', brand: '五常市农科院繁育中心', dosage: '2.5 kg/亩' },
          { name: '天然植物诱抗浸种生物剂', type: '生物制剂', brand: '极北生态', dosage: '50 ml/亩' },
        ],
      },
      {
        id: 'stage_02',
        stageCode: 'transplanting',
        stageName: '北斗导航无人驾驶插秧移栽',
        startDate: '2026-05-18',
        endDate: '2026-05-25',
        operator: '孙国强 (农机操作手)',
        description: '基于千寻位置北斗高精度RTK自动导航插秧机移栽，行株距28cm×14cm精准定植，每穴3-4苗，全田深水护苗快速返青。',
        photos: [
          'https://images.unsplash.com/photo-1586771107445-d3ca888129ff?w=600',
          'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=600',
        ],
        inputs: [
          { name: '大豆发酵精制生物有机底肥 (有机质≥65%)', type: '有机肥料', brand: '龙凤山生态肥业', dosage: '120 kg/亩' },
        ],
      },
      {
        id: 'stage_03',
        stageCode: 'managing',
        stageName: '田间生态管护与稻鸭绿色防控',
        startDate: '2026-06-01',
        endDate: '2026-08-31',
        operator: '李春山 (责任管理)',
        description: '采用“稻鸭共作+太阳能风吸式杀虫灯+性诱捕器”三重立体生物物理防虫体系，全程零化学农药、零化学除草剂，拉林河一级水源自流灌溉。',
        photos: [
          'https://images.unsplash.com/photo-1563245372-f21724e3856d?w=600',
          'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600',
        ],
        inputs: [
          { name: '苏云金杆菌 (Bt) 生物杀虫悬浮剂', type: '生物农药', brand: '绿源生防', dosage: '100 ml/亩' },
          { name: '深海海藻活性提取叶面肥', type: '生物叶面肥', brand: '海之星', dosage: '80 ml/亩' },
        ],
      },
      {
        id: 'stage_04',
        stageCode: 'harvesting',
        stageName: '适期秋收与北斗测产入仓',
        startDate: '2026-09-20',
        endDate: '2026-09-28',
        operator: '王海涛 (秋收组长)',
        description: '当全穗黄熟达95%时使用智能联合收割机收割，实时北斗测产计重，低温循环干燥塔降至14.0%安全水分后入常温恒湿粮仓。',
        photos: [
          'https://images.unsplash.com/photo-1530595467537-0b5996c41f2d?w=600',
          'https://images.unsplash.com/photo-1600336153113-d66c79de3e91?w=600',
        ],
        yieldStat: {
          yieldPerMu: 585.6,
          moistureRate: '14.0%',
          pureGrainRate: '99.2%',
        },
        inputs: [],
      },
    ];

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        traceCode: code,
        queryCount: 3,
        firstQueryTime: '2026-08-26 10:15:00',
        queryCity: '黑龙江省 · 哈尔滨市',
        orderInfo: {
          orderNumber: orderNo,
          customerName: customerName,
          customTag: '专属定制渠道单',
          deliveryDate: '2026-09-05',
        },
        productInfo: {
          brandName: entName,
          productName: prodName,
          spec: '5.0 kg (双砖真空锁鲜)',
          origin: '黑龙江省哈尔滨市五常市龙凤山水库核心示范区',
          grade: '国家优质一等 · 中国有机产品认证',
          germRetentionRate: '≥ 88.5%',
          shelfLife: '12 个月',
        },
        mesProductionTask: {
          taskCode: 'MES-PROD-20260820-001',
          taskName: '五常稻谷低温原粮脱壳初碾工单',
          riceVariety: '五优稻4号 (五常稻花香2号原种)',
          germRetention: '88.5%',
          millingRate: '66.0%',
          hullingPressure: '0.35 MPa 低温气压剥壳',
          moistureRate: '14.0%',
          workshopName: '1号原粮脱壳清选车间',
          lineName: '低温风冷脱壳色选1号线',
        },
        mesProcessingTask: {
          taskCode: 'MES-PROC-20260825-001',
          taskName: '五常有机胚芽米十万级洁净充氮真空分装工单',
          packageSpec: '5.0 kg (双砖真空锁鲜)',
          workshopName: '十万级洁净真空分装车间',
          lineName: '全自动充氮真空双砖包装1号线',
          residualOxygen: '< 0.3% 高阻隔锁鲜',
          qcInspector: '张建华 (高级品控师)',
        },
        baseInfo: {
          baseName: '五常市龙凤山水源地国家级有机水稻种植示范基地',
          landName: '五常核心示范区 · 1号稻田A区',
          area: 120,
          variety: '五优稻4号',
          soilType: '典型寒地黑钙土 (有机质 4.86g/kg)',
          waterSource: '拉林河源头龙凤山国家级湿地水库一级自流水源',
          manager: '李春山 (全国五一劳动奖章 / 30年有机种植首席农艺师)',
          weather: {
            temperature: '24.6°C',
            soilTemp: '21.2°C',
            humidity: '64%',
          },
        },
        farmingStages,
        notarizationInfo: notaryInfo,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Legacy Enterprise & Product Code endpoints
  http.get('/api/tracecode/enterprises', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: mockEnterpriseCodes,
        total: mockEnterpriseCodes.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  http.get('/api/tracecode/products', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: mockProductCodes,
        total: mockProductCodes.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),
];
